<!-- ExtractedItemsView.svelte -->
<script>
    import { createEventDispatcher } from 'svelte';
    import { addDiscountItem } from '../store.js';
    
    export let items = [];
    export let isProcessing = false;
    
    const dispatch = createEventDispatcher();
    
    // Function to add all items to the store
    function addAllItems() {
        items.forEach(item => {
            addDiscountItem(item);
        });
        
        // Notify parent component
        dispatch('itemsAdded', items);
    }
    
    // Function to add a single item to the store
    function addItem(item) {
        addDiscountItem(item);
        
        // Notify parent component
        dispatch('itemAdded', item);
    }
</script>

<div class="extracted-items">
    <div class="header">
        <h3>Extraherade varor ({items.length})</h3>
        {#if items.length > 0}
            <button class="add-all-button" on:click={addAllItems}>
                Lägg till alla
            </button>
        {/if}
    </div>
    
    {#if isProcessing}
        <div class="processing">
            <svg class="spinner" viewBox="0 0 50 50">
                <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
            </svg>
            <p>Analyserar PDF med Azure AI Vision...</p>
        </div>
    {:else if items.length === 0}
        <div class="empty-state">
            <p>Inga varor hittades i PDF:en.</p>
        </div>
    {:else}
        <div class="items-list">
            {#each items as item}
                <div class="item">
                    <div class="item-image">
                        {#if item.image}
                            <img src={item.image} alt={item.name} />
                        {:else}
                            <div class="placeholder-image">{item.name.charAt(0).toUpperCase()}</div>
                        {/if}
                    </div>
                    <div class="item-details">
                        <h4>{item.name}</h4>
                        <div class="item-price">
                            <span class="discount-price">{typeof item.discountPrice === 'number' ? item.discountPrice.toFixed(2) : '0.00'} kr</span>
                            {#if item.discount > 0 && typeof item.originalPrice === 'number'}
                                <span class="original-price">{item.originalPrice.toFixed(2)} kr</span>
                                <span class="discount-tag">-{item.discount}%</span>
                            {/if}
                        </div>
                        <div class="item-meta">
                            <span class="unit">{item.unit || 'st'}</span>
                            <span class="category">{item.category || 'övrigt'}</span>
                        </div>
                    </div>
                    <button class="add-button" on:click={() => addItem(item)}>Lägg till</button>
                </div>
            {/each}
        </div>
    {/if}
</div>

<style>
    .extracted-items {
        margin-top: 20px;
        padding: 15px;
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    }
    
    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
    }
    
    h3 {
        color: #333;
        margin: 0;
        font-size: 16px;
    }
    
    .add-all-button {
        background-color: #4caf50;
        color: white;
        border: none;
        padding: 6px 10px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.2s;
    }
    
    .add-all-button:hover {
        background-color: #3f9142;
    }
    
    .processing {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        background-color: #f5f5f5;
        border-radius: 5px;
    }
    
    .spinner {
        width: 24px;
        height: 24px;
        margin-right: 10px;
        animation: rotate 2s linear infinite;
    }
    
    .path {
        stroke: #1976d2;
        stroke-linecap: round;
        animation: dash 1.5s ease-in-out infinite;
    }
    
    @keyframes rotate {
        100% {
            transform: rotate(360deg);
        }
    }
    
    @keyframes dash {
        0% {
            stroke-dasharray: 1, 150;
            stroke-dashoffset: 0;
        }
        50% {
            stroke-dasharray: 90, 150;
            stroke-dashoffset: -35;
        }
        100% {
            stroke-dasharray: 90, 150;
            stroke-dashoffset: -124;
        }
    }
    
    .empty-state {
        padding: 15px;
        text-align: center;
        background-color: #f5f5f5;
        border-radius: 5px;
        color: #666;
    }
    
    .items-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 15px;
        max-height: 400px;
        overflow-y: auto;
    }
    
    .item {
        background-color: #f9f9f9;
        border-radius: 5px;
        padding: 10px;
        display: flex;
        flex-direction: column;
        position: relative;
        border: 1px solid #eee;
    }
    
    .item-image {
        width: 100%;
        height: 100px;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        background-color: #fff;
        border-radius: 4px;
        margin-bottom: 10px;
    }
    
    .item-image img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
    
    .placeholder-image {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #e0e0e0;
        color: #666;
        font-size: 24px;
        font-weight: bold;
    }
    
    .item-details {
        flex: 1;
    }
    
    h4 {
        margin: 0 0 5px 0;
        font-size: 14px;
        line-height: 1.3;
    }
    
    .item-price {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        margin-bottom: 5px;
    }
    
    .discount-price {
        font-weight: bold;
        color: #e91e63;
        margin-right: 8px;
    }
    
    .original-price {
        text-decoration: line-through;
        color: #999;
        font-size: 12px;
    }
    
    .discount-tag {
        background-color: #e91e63;
        color: white;
        font-size: 11px;
        padding: 2px 4px;
        border-radius: 3px;
        margin-left: 5px;
    }
    
    .item-meta {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #666;
        margin-bottom: 10px;
    }
    
    .add-button {
        background-color: #4caf50;
        color: white;
        border: none;
        padding: 6px 10px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        width: 100%;
        transition: background-color 0.2s;
    }
    
    .add-button:hover {
        background-color: #3f9142;
    }
</style>
